import React, { useEffect, useState } from 'react'
import {
    Avatar,
    Button,
    Divider,
    Flex,
    Icon,
    Menu,
    MenuButton,
    MenuGroup,
    MenuItem,
    MenuList,
    Spinner,
    Text,
    useColorModeValue,
    Box,
} from '@chakra-ui/react'
import { MdNotificationsNone } from 'react-icons/md'
import { FaEthereum } from 'react-icons/fa'
import { useAuth } from '../../contexts/AuthContext'
import { useNotifications } from '../../contexts/NotificationsBEContext'
import { ItemContent } from 'components/menu/ItemContent'
import { SidebarResponsive } from 'components/sidebar/Sidebar'
import routes from 'routes'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import axiosInstance from '../../api/axiosInstance'
import useHasPermission from '../../hooks/useHasPermission'

export default function HeaderLinks({ secondary }) {
    const { notifications, unreadCount, markAllAsRead, fetchMoreNotifications, hasMore } = useNotifications()
    const { user, logout, switchAccount } = useAuth()
    const hasPermission = useHasPermission()
    const isPremium = hasPermission('premium')
    const navigate = useNavigate()

    const [loadingMore, setLoadingMore] = useState(false)
    const [linkedAccounts, setLinkedAccounts] = useState([])
    const [loadingLinks, setLoadingLinks] = useState(true)

    const navbarIcon = useColorModeValue('gray.400', 'white')
    const menuBg = useColorModeValue('white', 'navy.800')
    const textColor = useColorModeValue('secondaryGray.900', 'white')
    const textColorBrand = useColorModeValue('brand.700', 'brand.400')
    const ethColor = useColorModeValue('gray.700', 'white')
    const borderColor = useColorModeValue('#E6ECFA', 'rgba(135, 140, 189, 0.3)')
    const ethBg = useColorModeValue('secondaryGray.300', 'navy.900')
    const ethBox = useColorModeValue('white', 'navy.800')
    const shadow = useColorModeValue(
        '14px 17px 40px 4px rgba(112, 144, 176, 0.18)',
        '14px 17px 40px 4px rgba(112, 144, 176, 0.06)'
    )
    const notificationColor = useColorModeValue('red.500', 'red.300')
    const counterColor = useColorModeValue('white', 'black')

    const handleMenuOpen = () => {
        markAllAsRead()
    }

    const handleLoadMore = async () => {
        setLoadingMore(true)
        await fetchMoreNotifications()
        setLoadingMore(false)
    }

    useEffect(() => {
        let mounted = true

        if (!isPremium) {
            setLoadingLinks(false)
            return
        }

        setLoadingLinks(true)
        axiosInstance
            .get('/accounts/linked')
            .then(({ data }) => {
                if (mounted) {
                    setLinkedAccounts(data.linked)
                }
            })
            .catch((_) => {
                console.log(_)
            })
            .finally(() => {
                if (mounted) {
                    setLoadingLinks(false)
                }
            })

        return () => {
            mounted = false
        }
    }, [isPremium])

    const onSwitch = accountId => {
        switchAccount(accountId)
    }

    return (
        <Flex
            w={{ sm: '100%', md: 'auto' }}
            alignItems='center'
            flexDirection='row'
            bg={menuBg}
            flexWrap={secondary ? { base: 'wrap', md: 'nowrap' } : 'unset'}
            p='10px'
            borderRadius='30px'
            boxShadow={shadow}
        >
            <Flex
                bg={ethBg}
                display={secondary ? 'flex' : 'none'}
                borderRadius='30px'
                ms='auto'
                p='6px'
                align='center'
                me='6px'
            >
                <Flex
                    align='center'
                    justify='center'
                    bg={ethBox}
                    h='29px'
                    w='29px'
                    borderRadius='30px'
                    me='7px'
                >
                    <Icon color={ethColor} w='9px' h='14px' as={FaEthereum} />
                </Flex>
                <Text w='max-content' color={ethColor} fontSize='sm' fontWeight='700' me='6px'>
                    1,924<Text as='span' display={{ base: 'none', md: 'unset' }}> ETH</Text>
                </Text>
            </Flex>

            <SidebarResponsive routes={routes} />

            <Menu onOpen={handleMenuOpen}>
                <MenuButton p='0px' position='relative'>
                    <Icon as={MdNotificationsNone} color={navbarIcon} w='24px' h='24px' me='10px' mt='6px' />
                    {unreadCount > 0 && (
                        <Box
                            position='absolute'
                            top='-5px'
                            right='5px'
                            bg={notificationColor}
                            color={counterColor}
                            borderRadius='full'
                            w='20px'
                            h='20px'
                            display='flex'
                            alignItems='center'
                            justifyContent='center'
                            fontSize='12px'
                            fontWeight='bold'
                        >
                            {unreadCount}
                        </Box>
                    )}
                </MenuButton>
                <MenuList
                    boxShadow={shadow}
                    p='20px'
                    borderRadius='20px'
                    bg={menuBg}
                    border='none'
                    mt='22px'
                    me={{ base: '30px', md: 'unset' }}
                    minW={{ base: 'unset', md: '400px', xl: '450px' }}
                    maxW={{ base: '360px', md: 'unset' }}
                    maxH='600px'
                    overflowY='auto'
                >
                    <Flex w='100%' mb='20px'>
                        <Text fontSize='md' fontWeight='600' color={textColor}>Notifications</Text>
                        <Text
                            fontSize='sm'
                            fontWeight='500'
                            color={textColorBrand}
                            ms='auto'
                            cursor='pointer'
                            onClick={markAllAsRead}
                        >
                            Mark all read
                        </Text>
                    </Flex>
                    <Flex flexDirection='column' mb='10px'>
                        {notifications.map(n => {
                            const date = dayjs(n.created_at).format('MMM D HH:mm')
                            return (
                                <MenuItem key={n.id} _hover={{ bg: 'none' }} _focus={{ bg: 'none' }} px='0' borderRadius='8px' mb='10px'>
                                    <ItemContent info={n.content} title={n.title} date={date} />
                                </MenuItem>
                            )
                        })}
                    </Flex>
                    {hasMore && (
                        <Button onClick={handleLoadMore} isLoading={loadingMore} variant='ghost' w='100%'>
                            Load More
                        </Button>
                    )}
                </MenuList>
            </Menu>

            <Menu>
                <MenuButton p='0px'>
                    <Avatar
                        _hover={{ cursor: 'pointer' }}
                        name={user?.name || 'ES Form user'}
                        bg='#11047A'
                        size='sm'
                        w='40px'
                        h='40px'
                        color='white'
                    />
                </MenuButton>
                <MenuList boxShadow={shadow} p='0px' mt='10px' borderRadius='20px' bg={menuBg} border='none'>

                    <Flex w='100%' mb='0px'>
                        <Text
                            ps='20px'
                            pt='16px'
                            pb='10px'
                            w='100%'
                            borderBottom='1px solid'
                            borderColor={borderColor}
                            fontSize='sm'
                            fontWeight='700'
                            color={textColor}
                        >
                            👋 Hey, {user?.name}
                        </Text>
                    </Flex>

                    {isPremium && (
                        <>
                            <MenuGroup title='Switch Account'>
                                {loadingLinks
                                    ? (
                                        <Flex justify='center' py='4'><Spinner size='sm' /></Flex>
                                    ) : linkedAccounts.length > 0
                                        ? linkedAccounts.map(acc => (
                                            <MenuItem
                                                key={acc.id}
                                                onClick={() => onSwitch(acc.id)}
                                                _hover={{ bg: 'gray.50' }}
                                                borderRadius='8px'
                                                px='14px'
                                            >
                                                <Text fontSize='sm'>{acc.email}</Text>
                                            </MenuItem>
                                        ))
                                        : (
                                            <Text fontSize='xs' color='gray.500' px='14px'>
                                                No linked accounts
                                            </Text>
                                        )
                                }
                            </MenuGroup>
                            <Divider />
                        </>
                    )}

                    <Flex flexDirection='column' py='10px'>
                        {isPremium && (
                            <MenuItem onClick={() => navigate('/admin/link-account')} _hover={{ bg: 'none' }} px='14px' borderRadius='8px'>
                                <Text fontSize='sm'>Link another account</Text>
                            </MenuItem>
                        )}
                        {!user?.is_employee && (
                            <MenuItem onClick={() => navigate('/admin/settings')} _hover={{ bg: 'none' }} px='14px' borderRadius='8px'>
                                <Text fontSize='sm'>Profile Settings</Text>
                            </MenuItem>
                        )}
                        <MenuItem onClick={() => navigate('/admin/change-password')} _hover={{ bg: 'none' }} px='14px' borderRadius='8px'>
                            <Text fontSize='sm'>Change Password</Text>
                        </MenuItem>
                        <MenuItem onClick={logout} _hover={{ bg: 'none' }} px='14px' borderRadius='8px' color='red.400'>
                            <Text fontSize='sm'>Log out</Text>
                        </MenuItem>
                    </Flex>
                </MenuList>
            </Menu>
        </Flex>
    )
}
